import React, { useRef, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { ResourcesPlannerProvider, useResourcesPlanner } from "./ResourcesPlannerContext";
import PlannerHeader from "./components/PlannerHeader";
import PlannerLeftPanel from "./components/PlannerLeftPanel";
import PlannerTimeline from "./components/PlannerTimeline";
import CreateEditTask from "../scopeOverview/CreateEditTask";
import Spin from "../../components/controls/spinner/Spin";
import styles from "./ResourcesPlanner.module.scss";
import { UsersIcon } from "../dashboard/icons/icons";
import FloatingAllocationCard from "./components/FloatingAllocationCard";
import moment from "moment";
import ConflictsToast from "./components/ConflictsToast";

const RESOURCE_ROW_MIN_HEIGHT = 52; // must match PlannerLeftPanel / PlannerTimeline
const LEFT_PANEL_HEADER_HEIGHT = 68; // fixed height of .leftPanelHeader

function countWorkingDays(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  if (end.isBefore(start, "day")) return 0;
  let count = 0;
  const cur = start.clone();
  while (cur.isSameOrBefore(end, "day")) {
    const dow = cur.day();
    if (dow !== 0 && dow !== 6) count++;
    cur.add(1, "day");
  }
  return count;
}

function PlannerContent() {
  const {
    isLoading,
    resources,
    taskModal,
    closeTaskModal,
    scrollToToday,
    floatingCards,
    closeFloatingCard,
    refetch,
    updatePageSize,
    viewType,
    registerScrollToTop,
    conflictsToasts,
    removeConflictsToast
  } = useResourcesPlanner();

  const timelineRef = useRef(null);
  const scrollWrapperRef = useRef(null);
  const plannerBodyRef = useRef(null);
  const hasScrolledToToday = useRef(false);

  // Register scroll-to-top so the context can reset vertical scroll on view change
  useEffect(() => {
    registerScrollToTop(() => {
      if (scrollWrapperRef.current) scrollWrapperRef.current.scrollTop = 0;
    });
  }, [registerScrollToTop]);

  // ── Dynamic page size based on available body height ─────────────────────
  useLayoutEffect(() => {
    const el = plannerBodyRef.current;
    if (!el) return;
    const calculate = () => {
      const height = el.clientHeight;
      if (height > 0) {
        updatePageSize(Math.max(5, Math.ceil((height - LEFT_PANEL_HEADER_HEIGHT) / RESOURCE_ROW_MIN_HEIGHT)));
      }
    };
    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updatePageSize]);

  // Recalculate explicitly on view type change (layout may shift)
  useEffect(() => {
    const el = plannerBodyRef.current;
    if (!el) return;
    const height = el.clientHeight;
    if (height > 0) {
      updatePageSize(Math.max(5, Math.ceil((height - LEFT_PANEL_HEADER_HEIGHT) / RESOURCE_ROW_MIN_HEIGHT)));
    }
  }, [viewType, updatePageSize]);

  const handleScrollToAllocation = useCallback(
    (date) => {
      if (timelineRef.current?.scrollToDate) {
        timelineRef.current.scrollToDate(date);
      }
    },
    []
  );

  // Auto-scroll to today on first load
  useEffect(() => {
    if (!isLoading && resources.length > 0 && !hasScrolledToToday.current) {
      hasScrolledToToday.current = true;
      // Small delay to ensure timeline has rendered
      setTimeout(() => {
        scrollToToday();
      }, 200);
    }
  }, [isLoading, resources, scrollToToday]);

  const plannerAllocationData = useMemo(() => {
    if (taskModal.taskId || !taskModal.resource || !taskModal.startDate || !taskModal.endDate) return null;
    const days = countWorkingDays(taskModal.startDate, taskModal.endDate);
    const hoursPerDay = 8;
    const totalHours = parseFloat((days * hoursPerDay).toFixed(2));
    return [
      {
        resource: {
          title: taskModal.resource.title || "",
          phoneNumber: taskModal.resource.phoneNumber || "",
          isExternal: taskModal.resource.isExternal || false,
          isActive: taskModal.resource.isActive !== undefined ? taskModal.resource.isActive : true,
          email: taskModal.resource.email || "",
          image: taskModal.resource.image || "",
          id: taskModal.resource.id,
          name: taskModal.resource.name,
        },
        startDate: taskModal.startDate,
        endDate: taskModal.endDate,
        hoursPerDay,
        totalHours,
        isIncludeWeekends: false,
        isDirty: true, // flag to indicate this is a new allocation for the task modal
      },
    ];
  }, [taskModal]);

  return (
    <div className={styles.plannerContainer}>
      <PlannerHeader />

      <div className={styles.plannerBody} ref={plannerBodyRef}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spin />
          </div>
        ) : resources.length === 0 ? (
          <div className={styles.emptyState}>
            <UsersIcon />
            <p>No resources found for the selected filters.</p>
          </div>
        ) : (
          <div className={styles.scrollWrapper} ref={scrollWrapperRef}>
            <div className={styles.scrollInner}>
              <PlannerLeftPanel onScrollToAllocation={handleScrollToAllocation} />
              <PlannerTimeline timelineRef={timelineRef} scrollWrapperRef={scrollWrapperRef} />
            </div>
          </div>
        )}
      </div>

      {/* Allocation Actions Conflicts Toasts */}
      {conflictsToasts.map((toast, index) => (
          <ConflictsToast
            key={index}
            data={toast}
            index={index}
            onClose={() => removeConflictsToast(index)}
          />
        ))
      }
      {/* Floating quick-view cards */}
      {floatingCards.map((card) => (
        <FloatingAllocationCard
          key={card.id}
          id={card.id}
          task={card.task}
          scope={card.scope}
          project={card.project}
          allocation={card.allocation}
          position={card.position}
          onClose={closeFloatingCard}
        />
      ))}

      {taskModal.isOpen && (
        <CreateEditTask
          isOpen={taskModal.isOpen}
          isEdit={taskModal.isEdit}
          taskId={taskModal.taskId}
          projectID={taskModal.project?.id}
          projectName={taskModal.project?.name}
          scopeID={taskModal.scope?.id}
          scopeName={taskModal.scope?.name}
          onClose={closeTaskModal}
          onSave={refetch}
          plannerAllocationData={plannerAllocationData}
        />
      )}
    </div>
  );
}

const ResourcesPlanner = () => {
  return (
    <ResourcesPlannerProvider>
      <PlannerContent />
    </ResourcesPlannerProvider>
  );
};

export default ResourcesPlanner;